import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {mergeMap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';

/**
 * Rc api service
 * make API calls, support cache
 */
@Injectable({
    providedIn: 'root'
})
export class RcApiService {

    /**
     * api base url
     */
    private _apiBaseUrl = '';

    /**
     * cache data, save responses in cache for future use
     */
    private cacheData: any = {};

    constructor(protected http: HttpClient) {}

    /**
     * set base url of API
     */
    set apiBaseUrl(val: string) {
        this._apiBaseUrl = val;
    }

    /**
      make a GET request
     * @param actionName
     * @param requestParams
     * @param force
     * @param customCallHeaders
     * @returns Observable
     */
    get(actionName, requestParams = {}, force = true, customCallHeaders?: Object): Observable<Object> {
        const params = new HttpParams({
            fromObject: requestParams
        });
        const actionKey = `${actionName}_${JSON.stringify(requestParams)}`;
        const options = {
            params: params,
            headers: this.getHeaders(customCallHeaders)
        };

        if (!force && this.cacheData[actionKey]) {
            return of(this.cacheData[actionKey]);
        }

        const apiFullPath = `${this.getBaseUrl()}${actionName}`;

        return this.http.get(apiFullPath, options).pipe(
            mergeMap((result) => {
                this.cacheData[actionKey] = result;
                return of(result);
            })
        );
    }


    /**
     * make a POST request
     * @param actionName
     * @param requestParams
     * @param customCallHeaders
     * @returns Observable
     */
    post(actionName, requestParams = {}, customCallHeaders?: Object): Observable<Object>  {
        return this.http.post(`${this.getBaseUrl()}${actionName}`, requestParams, {headers: this.getHeaders(customCallHeaders)});
    }
    /**
     * make a PUT request
     * @param actionName
     * @param requestParams
     * @param customCallHeaders
     * @returns Observable
     */
    put(actionName, requestParams = {}, customCallHeaders?: Object): Observable<Object>  {
        return this.http.put(`${this.getBaseUrl()}${actionName}`, requestParams, {headers: this.getHeaders(customCallHeaders)});
    }

    /**
     * make a DELETE request
     * @param actionName
     * @param requestParams
     * @param customCallHeaders
     * @returns Observable
     */
    delete(actionName, requestParams = {},  customCallHeaders?: Object): Observable<Object>  {
        return this.http.delete(`${this.getBaseUrl()}${actionName}`, {headers: this.getHeaders(customCallHeaders), params: requestParams});
    }

    /**
     * get base url - can be extended / override by a child
     * @returns string
     */
    protected getBaseUrl(): string {
        return this._apiBaseUrl;
    }

    /**
     * get request headers - can be extended / override by child class
     * @returns HttpHeaders
     */
    protected getHeaders(customCallHeaders: Object): HttpHeaders {
        let headers = {};
        this.setDefaultHeaders(headers);
        this.setCustomHeaders(headers);
        if (customCallHeaders) {
            headers = Object.assign({}, headers, customCallHeaders);
        }
        return new HttpHeaders(headers);
    }

    protected setDefaultHeaders(headers) {
      headers['Accept'] = 'application/json';
      headers['Cache-Control'] = 'no-cache, no-store';
      headers['Content-Type'] = 'application/json; charset=utf-8';
      headers['Pragma'] = 'no-cache';
      headers['Expires'] = 'Sat, 01 Jan 2000 00:00:00 GMT';
    }

    protected setCustomHeaders(headers) {

      return;
    }

    /**
     * clear api cache
     */
    public clearCache(): void {
        this.cacheData = {};
    }
}
